import {toast} from '@wandb/components';
import {useCallback} from 'react';
import {v4 as uuidv4} from 'uuid';

import {useDraft} from '../../../agentChat/composer';
import {useActiveThread} from '../../../agentChat/store';
import {
  type ComposerFile,
  type FailedComposerImage,
  fileToImagePart,
  type ImagePartResult,
  type ImageValidationReason,
  MAX_IMAGE_BYTES,
  MAX_IMAGES_PER_TURN,
  MAX_TOTAL_IMAGE_BYTES,
  toComposerImage,
  validateImageParts,
} from './imageParts';

type RejectionReason =
  | Extract<ImagePartResult, {ok: false}>['reason']
  | ImageValidationReason;

const MAX_IMAGE_MB = Math.round(MAX_IMAGE_BYTES / (1024 * 1024));
const MAX_TOTAL_IMAGE_MB = Math.round(MAX_TOTAL_IMAGE_BYTES / (1024 * 1024));

const REJECTION_MESSAGES: Record<RejectionReason, string> = {
  'unsupported-type': 'Unsupported image type. Use JPEG, PNG, GIF, or WebP.',
  'too-large': `Each image must be under ${MAX_IMAGE_MB} MB.`,
  'read-failed': 'Could not read that image. Try a different file.',
  'too-many': `You can attach up to ${MAX_IMAGES_PER_TURN} images.`,
  'total-too-large': `Those images exceed the total attachment size limit of ${MAX_TOTAL_IMAGE_MB} MB.`,
};

export function useComposerImageUpload() {
  const activeThread = useActiveThread();
  const threadId = activeThread?.id ?? null;
  const {addFiles, addFailedFiles} = useDraft(threadId);

  const addComposerImages = useCallback(
    // `extraReasons` lets callers fold in rejections detected upstream (e.g.
    // react-dropzone filtering by `accept`) into the same toast pass.
    async (dropped: File[], extraReasons: RejectionReason[] = []) => {
      const results = await Promise.all(dropped.map(fileToImagePart));

      const accepted: ComposerFile[] = [];
      const failed: FailedComposerImage[] = [];
      // Dedupe by reason so multiple rejected files yield one toast per cause.
      const reasons = new Set<RejectionReason>(extraReasons);
      results.forEach((result, i) => {
        if (result.ok) {
          accepted.push(toComposerImage(result.part));
        } else if (result.reason === 'read-failed') {
          // Keep the file to render an error image thumbnail.
          failed.push({
            id: uuidv4(),
            previewUrl: URL.createObjectURL(dropped[i]),
          });
        } else {
          reasons.add(result.reason);
        }
      });

      if (accepted.length > 0) {
        // Validate the whole set (already-attached + new) inside the store's
        // commit, so two concurrent drops can't both pass a stale snapshot.
        let rejection: ImageValidationReason | undefined;
        addFiles(accepted, next => {
          const validation = validateImageParts(next);
          if (!validation.ok) {
            rejection = validation.reason;
          }

          return validation.ok;
        });
        if (rejection) {
          reasons.add(rejection);
        }
      }

      if (failed.length > 0) {
        addFailedFiles(failed);
      }

      for (const reason of reasons) {
        toast.error(REJECTION_MESSAGES[reason]);
      }
    },
    [addFiles, addFailedFiles]
  );

  // react-dropzone `onDrop`: files filtered out by `accept` arrive as
  // `rejectedFiles` (never in `accepted`), so map them to `unsupported-type`.
  const onDrop = useCallback(
    (accepted: File[], rejectedFiles: File[]) => {
      const extraReasons: RejectionReason[] =
        rejectedFiles.length > 0 ? ['unsupported-type'] : [];
      void addComposerImages(accepted, extraReasons);
    },
    [addComposerImages]
  );

  return {addComposerImages, onDrop};
}
